import { debounce, isEmpty, isEqual, noop, startsWith } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import KEY_CODES from 'commons-forms/constants/keycodes.js';
import ORGANIZATIONS from 'commons-forms/constants/organizations.js';
import SuggestionList from 'commons-forms/components/InputAutoComplete/SuggestionList/SuggestionList.jsx';
import { LocaleService, WindowProxy, ConfigsService } from '@sm360/widget-commons-core-services';
import { AnalyticsService, REDIRECTIONS, TRACKING_TAGS as TAGS } from '@sm360/widget-commons-general';

import 'commons-forms/components/InputAutoComplete/InputAutoComplete.scss';

class InputAutoComplete extends Component {
    constructor(props) {
        super(props);

        this.state = {
            activeItemIndex: -1,
            value: '',
            items: props.items,
            hasFocus: false,
            inError: false,
            isLoading: false,
        };

        this.getSuggestionPlaceholder = this.getSuggestionPlaceholder.bind(this);
        this.onBlur = debounce(this.onBlur, 300).bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onInputChanged = this.onInputChanged.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onSelectedSuggestion = this.onSelectedSuggestion.bind(this);
        this.refreshingItems = debounce(this.refreshingItems, props.debounceRefreshTime).bind(this);
        this.resetValue = this.resetValue.bind(this);
        this.selectNextElement = this.selectNextElement.bind(this);
        this.selectPreviousElement = this.selectPreviousElement.bind(this);
        this.updateValue = this.updateValue.bind(this);
        this.validateInput = this.validateInput.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        const newState = {};

        if (!isEqual(nextProps.items, this.state.items)) {
            newState.items = nextProps.items;
        }
        if (!isEqual(nextProps.value, this.state.value) && !this.state.hasFocus) {
            newState.value = nextProps.value;
        }
        if (!isEqual(nextProps.isLoading, this.state.isLoading) && this.state.hasFocus) {
            newState.isLoading = nextProps.isLoading;
        }
        if (!isEmpty(newState)) {
            this.setState(newState, this.validateInput);
        }
    }

    onBlur() {
        this.setState({ hasFocus: false });
    }

    onFocus() {
        this.setState({ hasFocus: true });
    }

    onInputChanged(event) {
        this.updateValue(event.target.value);
    }

    onSelectedSuggestion(value, handler = undefined) {
        this.updateValue(value);
        this.onBlur();
        this.props.onSelectedOption(value, handler);
    }

    onKeyUp(event) {
        if (!this.state.hasFocus) {
            this.onFocus();
        }
        switch (event.keyCode) {
            case KEY_CODES.BACKSPACE:
                if (!this.state.value.length) {
                    this.resetValue();
                }
                break;
            default:
                break;
        }
    }

    onKeyDown(event) {
        if (!this.state.hasFocus) {
            this.onFocus();
        }

        switch (event.keyCode) {
            case KEY_CODES.TAB:
                this.onSelectedSuggestion(this.state.value, KEY_CODES.TAB);
                break;
            case KEY_CODES.ENTER:
                event.preventDefault();
                this.onSelectedSuggestion(this.state.value, KEY_CODES.ENTER);
                break;
            case KEY_CODES.UP:
                if (this.state.items.length > 0) {
                    this.selectPreviousElement();
                    event.preventDefault();
                }
                break;
            case KEY_CODES.DOWN:
                if (this.state.items.length > 0) {
                    this.selectNextElement();
                    event.preventDefault();
                }
                break;
            default:
                break;
        }
    }

    onCantFindVehicleClick(vehicleInventoryId) {
        const windowProxy = WindowProxy.get();
        const locale = LocaleService.getSimpleLocale();
        const organizationId = ConfigsService.getOrganizationId();
        const isShowroomV25Enabled = ConfigsService.isShowroomV25Enabled();

        // TODO : Dilawri OrgId condition to be removed when deploy to all dealers
        const isDilawri = organizationId === ORGANIZATIONS.DILAWRI;
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_CANT_FIND_VEHICLE_LINK());

        if (windowProxy) {
            if (isDilawri) {
                const extraParams = vehicleInventoryId ? `?carId=${vehicleInventoryId}` : '';
                windowProxy.location.href = `${REDIRECTIONS[locale].tradeInAppraisalV25Dilawri}${extraParams}`;
            } else {
                windowProxy.location.href = isShowroomV25Enabled
                    ? REDIRECTIONS[locale].tradeInAppraisalV25
                    : REDIRECTIONS[locale].tradeInAppraisal;
            }
        }
    }

    getSuggestionPlaceholder() {
        const { value, items } = this.state;
        const hasItems = !Array.isArray(items) || !items.length;
        return hasItems && (startsWith(items[0].value, value) || !value) ? items[0].value : '';
    }

    refreshingItems(value, callback = noop) {
        let items = [];
        if (value) {
            items = this.props.refreshItems(value) || items;
        }

        this.setState({ items }, callback);
    }

    resetValue() {
        this.props.onResetValue();
        if (this.inputAutoComplete) this.inputAutoComplete.focus();
        this.updateValue('');
    }

    selectNextElement() {
        const { items } = this.state;
        const nextIndex = this.state.activeItemIndex + 1 >= items.length ? 0 : this.state.activeItemIndex + 1;
        this.updateSelectedElement(nextIndex);
    }

    selectPreviousElement() {
        const { items } = this.state;
        const nextIndex = this.state.activeItemIndex - 1 < 0 ? items.length - 1 : this.state.activeItemIndex - 1;
        this.updateSelectedElement(nextIndex);
    }

    updateSelectedElement(index) {
        const { items } = this.state;
        this.setState({
            value: items[index].value,
            activeItemIndex: index,
        });
    }

    updateValue(value) {
        this.setState({ value }, () => this.refreshingItems(value, this.validateInput));
    }

    validateInput() {
        // Updates the state and resolves with a boolean indicating if the input is valid.
        return new Promise((resolve) => {
            if (!this.state.isLoading) {
                this.setState(
                    (oldState) => ({
                        inError: !(oldState.items && oldState.items.length > 0 && oldState.value),
                    }),
                    () => {
                        resolve(!this.state.inError);
                    }
                );
            }
        });
    }

    render() {
        const {
            name,
            suggestionsToDisplay,
            iconClassName,
            errorLabel,
            isRequired,
            classNames,
            autoCompleteLabel,
            dataTestPrefix,
            cantFindVehicleLabel,
            displayOnWrapper,
            placeholder,
            instantEstimateLabel,
            isBarWrapper,
        } = this.props;

        const { value, activeItemIndex, items, hasFocus, inError } = this.state;

        const parentClasses = ['InputAutoComplete'];
        parentClasses.push(classNames);
        const inputClasses = ['input-control'];
        const showSuggestion = !isEmpty(items) && hasFocus;
        let placeholderValue = '';

        if (isRequired) {
            inputClasses.push('required');
        }
        if (inError) {
            parentClasses.push('field-error');
        }
        if (showSuggestion) {
            inputClasses.push('SuggestionList--open');
        }
        if (showSuggestion && this.state.value !== '') {
            placeholderValue = this.getSuggestionPlaceholder();
        }

        // Link for cant find vehicle, either above the box or below the box
        // As a part of new tradeIn
        const cantFindVehicleLink = (
            <p className="cant-find-vehicle-link">
                <a onClick={() => this.onCantFindVehicleClick(this.props.vehicleInventoryId)}>{cantFindVehicleLabel}</a>
            </p>
        );

        const organizationId = ConfigsService.getOrganizationId();

        const isDilawriVersion = organizationId === ORGANIZATIONS.DILAWRI;

        return (
            <div
                className={parentClasses.join(' ')}
                data-test={dataTestPrefix ? `${dataTestPrefix}.cbb-auto-complete` : 'cbb-auto-complete'}
            >
                {!displayOnWrapper &&
                    !isDilawriVersion &&
                    (instantEstimateLabel !== '' && (
                        <p className={`instant-estimate-text ${!isBarWrapper && 'enlarge'}`}>{instantEstimateLabel}</p>
                    ))}
                {!displayOnWrapper && isDilawriVersion && <p className="auto-complete-text">{autoCompleteLabel}</p>}
                <div className="InputAutoComplete--input" data-placeholder={placeholderValue}>
                    {this.state.isLoading ? (
                        <icon className="icon-refresh loadingResult" />
                    ) : (
                        <icon className={iconClassName} />
                    )}
                    <input
                        id={name}
                        type="text"
                        name={name}
                        aria-label="Vehicles"
                        className={inputClasses.join(' ')}
                        value={value}
                        onChange={this.onInputChanged}
                        onKeyUp={this.onKeyUp}
                        onKeyDown={this.onKeyDown}
                        onFocus={this.onFocus}
                        onBlur={this.onBlur}
                        autoComplete="off"
                        required={isRequired}
                        placeholder={placeholder}
                        ref={(input) => {
                            this.inputAutoComplete = input;
                        }}
                    />
                    {(!!value || !!showSuggestion) && (
                        <icon className="delete-icon icon-x-config" role="button" onClick={this.resetValue} />
                    )}
                    {inError && <p className="errors-input">{errorLabel}</p>}
                </div>
                {!displayOnWrapper && !isDilawriVersion && <p className="auto-complete-text">{autoCompleteLabel}</p>}
                {!displayOnWrapper && cantFindVehicleLink}
                {showSuggestion && (
                    <SuggestionList
                        items={items}
                        onSelectedOption={this.onSelectedSuggestion}
                        activeElementIndex={activeItemIndex}
                        suggestionsToDisplay={suggestionsToDisplay}
                    />
                )}
            </div>
        );
    }
}

InputAutoComplete.defaultProps = {
    classNames: [],
    debounceRefreshTime: 400,
    errorLabel: '',
    isRequired: false,
    onResetValue: noop,
    placeholder: '',
    autoCompleteLabel: '',
    suggestionsToDisplay: 10,
    value: '',
    dataTestPrefix: '',
    instantEstimateLabel: '',
    isBarWrapper: false,
};

InputAutoComplete.propTypes = {
    classNames: PropTypes.arrayOf(PropTypes.string),
    debounceRefreshTime: PropTypes.number,
    errorLabel: PropTypes.string,
    isLoading: PropTypes.bool.isRequired,
    iconClassName: PropTypes.string.isRequired,
    isRequired: PropTypes.bool,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        }).isRequired
    ).isRequired,
    name: PropTypes.string.isRequired,
    onResetValue: PropTypes.func,
    onSelectedOption: PropTypes.func.isRequired,
    autoCompleteLabel: PropTypes.string,
    refreshItems: PropTypes.func.isRequired,
    suggestionsToDisplay: PropTypes.number,
    value: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    dataTestPrefix: PropTypes.string,
    placeholder: PropTypes.string,
    instantEstimateLabel: PropTypes.string,
    isBarWrapper: PropTypes.bool,
};

InputAutoComplete.displayName = 'InputAutoComplete';

export default InputAutoComplete;
